/* Reusable full-bleed keen slider. Added by Oleg @ IAKOE.
   <ts-bleed-slider> wraps a .keen-slider track and inits keen with a centered
   active slide on mobile (origin: 'center') and a "shift left" origin on desktop
   (the kit's <ia-slider> can't do centered/origin). All values come from data-*
   attributes (single source of truth = section settings). Used by Ingredients
   and Our community. */

import KeenSlider from 'ia-keen-slider';

if (!customElements.get('ts-bleed-slider')) {
  customElements.define(
    'ts-bleed-slider',
    class TsBleedSlider extends HTMLElement {
      connectedCallback() {
        if (this._slider) return;
        const track = this.querySelector('.keen-slider');
        if (!track) return;

        const d = this.dataset;
        const perViewDesktop = parseFloat(d.itemsDesktop);
        const perViewMobile = parseFloat(d.itemsMobile);
        // "Shift left" = how far the row moves left, in % of one card.
        // keen's origin is in container units, so divide the card fraction by perView.
        const shiftLeft = (parseFloat(d.shift) || 0) / 100;

        // Loop, scroll range and arrows all follow ONE question: is anything hidden?
        // Positions in card widths, desktop: slide k sits at [k - shift, k + 1 - shift] and
        // the screen shows [0, perView]. Anything beyond perView slides is (partly) off
        // screen, the card cut on the left by the shift included.
        //  - a loop wraps without a hole only when the slides outnumber the span they fill
        //    (view + shift); with fewer, the originals are CLONED until they do (5 cards at
        //    4.7 per view shifted 45% span 5.15 cards: without clones the wrapped row showed
        //    an empty gap, and without a loop it could not scroll at all). Not in the theme
        //    editor: a cloned block would confuse block selection there.
        //  - without loop the row may travel just far enough to show the last card (keen's
        //    own limit with a shifted origin let the last card reach the LEFT edge and the
        //    rest of the row stood empty); under half a card hidden = the row stays still;
        //  - nothing to scroll (every slide fits) = no drag and no arrows (data-static).
        const loop = d.loop === 'true';
        const originals = Array.from(track.children);
        let count = originals.length;
        const needed = Math.max(perViewDesktop + shiftLeft, perViewMobile + 1);
        // Clones only when the originals already overflow the DESKTOP view: the clones are
        // in the DOM on every breakpoint, and a desktop row that fits would show them side by
        // side as duplicates. Such a row just scrolls to its end on a phone, no loop.
        const wantsLoop = loop && count > perViewDesktop;
        if (wantsLoop && count <= needed && !(window.Shopify && window.Shopify.designMode)) {
          while (count <= needed) {
            originals.forEach((slide) => {
              const clone = slide.cloneNode(true);
              clone.setAttribute('aria-hidden', 'true');
              clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
              track.appendChild(clone);
            });
            count += originals.length;
          }
        }
        const scrollsDesktop = count > perViewDesktop;
        const scrollsMobile = count > perViewMobile;
        const loopsDesktop = loop && scrollsDesktop && count > perViewDesktop + shiftLeft;
        const loopsMobile = loop && scrollsMobile && count >= perViewMobile + 1;
        const hiddenDesktop = count - perViewDesktop - shiftLeft;
        const maxIdxDesktop = hiddenDesktop > 0.5 ? Math.ceil(hiddenDesktop) : 0;
        const dragsDesktop = loopsDesktop || maxIdxDesktop > 0;

        // spacing stays 0 here ON PURPOSE — the gap is CSS padding inside each slide.
        // keen draws spacing by TRANSFORM (its offset sums widths only, assuming the
        // slides sit flush), so anything keen spaces has to jump into place once the JS
        // boots, and a CSS gap/margin would be added on top and double it. With 0 the
        // resting transform is 0: the slide the browser lays out is the final one.
        this._slider = new KeenSlider(track, {
          loop: loopsDesktop,
          drag: dragsDesktop,
          range: loopsDesktop ? undefined : { min: 0, max: maxIdxDesktop },
          slides: {
            perView: perViewDesktop,
            spacing: 0,
            origin: -shiftLeft / perViewDesktop,
          },
          breakpoints: {
            '(max-width: 1023px)': {
              loop: loopsMobile,
              drag: scrollsMobile,
              range: loopsMobile ? undefined : { min: 0, max: Math.max(0, count - 1) },
              slides: {
                perView: perViewMobile,
                spacing: 0,
                origin: 'center',
              },
            },
          },
        });

        this._mobile = window.matchMedia('(max-width: 1023px)');
        this._syncStatic = () => {
          this.toggleAttribute('data-static', this._mobile.matches ? !scrollsMobile : !dragsDesktop);
        };
        this._syncStatic();
        this._mobile.addEventListener('change', this._syncStatic);

        // Trackpad and horizontal wheel: keen has no wheel support, so a desktop row that
        // only drags with a pressed mouse reads as static (the client's report on the
        // landing Benefits row). A sideways gesture moves ONE card. The gesture ends when its
        // events stop for 200ms, OR when a new swipe starts inside the previous one's
        // inertia: inertia only fades, so the speed rising again after it began to fade, or
        // a turn the other way, is a new swipe (quick swipes in a row were swallowed before).
        // The first 30px of travel decide the direction; vertical scroll is left to the page.
        // Same rule as the kit slider (ia-slider.js bindWheel).
        let travel = 0;
        let moved = false;
        let peak = 0;
        let last = 0;
        let fading = false;
        const reset = () => {
          travel = 0;
          moved = false;
          peak = 0;
          last = 0;
          fading = false;
        };
        this.onWheel = (event) => {
          if (this.hasAttribute('data-static')) return;
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          const speed = Math.abs(event.deltaX);
          if (moved) {
            const turned = Math.sign(event.deltaX) !== Math.sign(travel) && speed > 6;
            const rising = fading && speed > last * 1.5 + 3;
            if (turned || rising) reset();
          }
          if (speed > peak) peak = speed;
          if (speed < peak * 0.7) fading = true;
          last = speed;
          clearTimeout(this._wheelTimer);
          this._wheelTimer = setTimeout(reset, 200);
          if (moved) return;
          travel += event.deltaX;
          if (Math.abs(travel) < 30) return;
          moved = true;
          if (travel > 0) this._slider.next();
          else this._slider.prev();
        };
        this.addEventListener('wheel', this.onWheel, { passive: false });

        // Optional arrows (Liquid renders them only when the caller asks).
        this.querySelector('[data-bs-prev]')?.addEventListener('click', () => this._slider.prev());
        this.querySelector('[data-bs-next]')?.addEventListener('click', () => this._slider.next());
      }

      disconnectedCallback() {
        if (this._mobile) this._mobile.removeEventListener('change', this._syncStatic);
        if (this.onWheel) this.removeEventListener('wheel', this.onWheel);
        clearTimeout(this._wheelTimer);
        if (this._slider) {
          this._slider.destroy();
          this._slider = null;
        }
      }
    }
  );
}
